import { DirectSignupFiltersFormType } from 'domains/signupDirect/schemas/filtersSchema';
import { captureException } from '@sentry/react';
import { SignupError } from 'utils/errors/debuggableError';
import { AdultConcernOption, PediatricConcernOption } from 'domains/signupDirect/misc/communicationConcerns';
import { AdultConcern, PediatricConcern } from 'domains/signupv4/Mini-Screener/data/areaDetection';

/**
 * Single source of truth for determining if a lead can go to split evaluations
 */

/** Global ON/OFF switch */
const SPLIT_EVALS_ENABLED = true;

/** Specialties that are not suitable for split evaluations */
const SPLIT_EVAL_EXCLUDED_SPECIALTIES = [
  'Concentration and memory',
  'Using a device or speech app for communication',
  'Assisted communication device',
] as (AdultConcern | PediatricConcern)[]

export const filtersAllowSplitEvaluation = ({
  paymentMethod,
  clinicalConcerns,
}: {
  paymentMethod?: DirectSignupFiltersFormType['paymentMethod'];
  clinicalConcerns?: PediatricConcernOption[] | AdultConcernOption[];
}) => {
  if (!SPLIT_EVALS_ENABLED) {
    return { allowed: false, reason: 'Split evaluations are disabled globally' };
  }

  if (!paymentMethod) {
    return { allowed: false, reason: 'Incomplete form' };
  }

  if (paymentMethod.type === 'otherInsurance') {
    return { allowed: false, reason: `Picked can't find my insurance` };
  }

  if (!clinicalConcerns) {
    return { allowed: false, reason: 'Incomplete form - clinical concerns'};
  }

  // Check if any selected clinical concerns are excluded from split evaluations
  if (clinicalConcerns?.some(concern => 
    SPLIT_EVAL_EXCLUDED_SPECIALTIES.includes(concern.key)
  )) {
    return { allowed: false, reason: 'Specialty not suitable for split evaluations' };
  }

  if (paymentMethod.type === 'insurance') {
    if (paymentMethod.insuranceData.evaluationOnly) {
      return { allowed: false, reason: 'Evaluation-only insurances are not allowed' };
    }

    if (!paymentMethod.insuranceData.allowSplitEvaluations) {
      return {
        allowed: false,
        reason: `Split evaluations is not allowed for ${paymentMethod.insuranceData.friendlyPayerName}`,
      };
    }

    if (paymentMethod.insuranceData.insuranceType === 'Medicaid') {
      return { allowed: false, reason: 'Medicaid is not allowed' };
    }
    if (paymentMethod.insuranceData.insuranceType === 'Medicare') {
      return { allowed: false, reason: 'Medicare is not allowed' };
    }
    if (paymentMethod.insuranceData.insuranceType === 'Commercial') {
      return { allowed: true, reason: 'Commercial insurance is allowed' };
    }
  }

  if (paymentMethod.type === 'privatePay') {
    return { allowed: true, reason: 'Private pay is allowed' };
  }

  captureException(new SignupError(`Reached end of filtersAllowSplitEvaluation without a return`, {
    debugData: {
      paymentMethod,
      clinicalConcerns,
    },
  }));
  return { allowed: false, reason: 'Default exclusion reason' };
};
